import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { events, streams } from '../../lib/api';

const EMPTY_FORM = {
  title: '', description: '', location: '', category: '',
  tags: [], isPublic: true, scheduledStart: '', scheduledEnd: '',
  streamIds: [],
};

function EventsPage() {
  const [eventList, setEventList] = useState([]);
  const [streamList, setStreamList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([loadEvents(), loadStreams()]).then(() => setLoading(false));
  }, []);

  const loadEvents = async () => {
    try {
      const res = await events.getAll();
      setEventList(res.data.events || []);
    } catch (err) { console.error('Failed to load events:', err); }
  };

  const loadStreams = async () => {
    try {
      const res = await streams.getAll();
      setStreamList(res.data.streams || []);
    } catch (err) { console.error('Failed to load streams:', err); }
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };

  const openEdit = (evt) => {
    setForm({
      title: evt.title || '', description: evt.description || '',
      location: evt.location || '', category: evt.category || '',
      tags: evt.tags || [], isPublic: evt.isPublic ?? true,
      scheduledStart: evt.scheduledStart ? new Date(evt.scheduledStart).toISOString().slice(0, 16) : '',
      scheduledEnd: evt.scheduledEnd ? new Date(evt.scheduledEnd).toISOString().slice(0, 16) : '',
      streamIds: evt.streams?.map(s => s.streamId) || [],
    });
    setEditingId(evt.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        scheduledStart: form.scheduledStart ? new Date(form.scheduledStart).toISOString() : null,
        scheduledEnd: form.scheduledEnd ? new Date(form.scheduledEnd).toISOString() : null,
      };
      if (editingId) await events.update(editingId, payload);
      else await events.create(payload);
      setShowForm(false);
      await loadEvents();
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save event');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!confirm(`Delete event "${title}"?`)) return;
    try { await events.delete(id); await loadEvents(); }
    catch (err) { alert('Failed to delete event'); }
  };

  const handleGoLive = async (id) => {
    try { await events.goLive(id); await loadEvents(); }
    catch (err) { alert('Failed to go live'); }
  };

  const handleEnd = async (id) => {
    try { await events.end(id); await loadEvents(); }
    catch (err) { alert('Failed to end event'); }
  };

  const toggleStream = (streamId) => {
    setForm(prev => ({
      ...prev,
      streamIds: prev.streamIds.includes(streamId)
        ? prev.streamIds.filter(id => id !== streamId)
        : [...prev.streamIds, streamId]
    }));
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (loading) return <p className="text-neutral-500">Loading events...</p>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Events</h1>
        <button onClick={openAdd} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
          New Event
        </button>
      </div>

      {eventList.length === 0 ? (
        <div className="text-center py-16 text-neutral-500">
          <p className="text-lg mb-2">No events</p>
          <p className="text-sm">Create an event to group your cameras</p>
        </div>
      ) : (
        <div className="space-y-3">
          {eventList.map(evt => (
            <div key={evt.id} className="p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-3 mb-2">
                <StatusBadge status={evt.status} />
                <Link to={`/events/${evt.id}`} className="font-semibold text-lg hover:text-blue-500 transition-colors flex-1">
                  {evt.title}
                </Link>
                <div className="flex gap-2">
                  {evt.status === 'upcoming' && (
                    <button onClick={() => handleGoLive(evt.id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">
                      Go Live
                    </button>
                  )}
                  {evt.status === 'live' && (
                    <>
                      <Link to={`/events/${evt.id}/dashboard`} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                        Dashboard
                      </Link>
                      <button onClick={() => handleEnd(evt.id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-neutral-600 text-white hover:bg-neutral-700 transition-colors">
                        End
                      </button>
                    </>
                  )}
                  <button onClick={() => openEdit(evt)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(evt.id, evt.title)} className="px-3 py-1.5 text-xs font-semibold rounded-lg text-red-500 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-neutral-500">
                {evt.streams?.map(s => (
                  <span key={s.streamId} className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
                    {s.label || s.name}
                  </span>
                ))}
                {evt.scheduledStart && (
                  <span>{new Date(evt.scheduledStart).toLocaleString('da-DK', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                )}
                {evt.location && <span>{evt.location}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <form onSubmit={handleSave} className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-6">
            <h2 className="text-xl font-bold mb-4">{editingId ? 'Edit Event' : 'New Event'}</h2>

            <div className="space-y-4">
              <FormField label="Title" value={form.title} onChange={v => updateForm('title', v)} required />
              <FormField label="Description" value={form.description} onChange={v => updateForm('description', v)} multiline />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Start</label>
                  <input type="datetime-local" value={form.scheduledStart} onChange={e => updateForm('scheduledStart', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">End</label>
                  <input type="datetime-local" value={form.scheduledEnd} onChange={e => updateForm('scheduledEnd', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Location" value={form.location} onChange={v => updateForm('location', v)} />
                <FormField label="Category" value={form.category} onChange={v => updateForm('category', v)} />
              </div>

              {/* Camera picker */}
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-2">Cameras</label>
                <div className="space-y-2">
                  {streamList.map(s => (
                    <label key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.streamIds.includes(s.id) ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'}`}>
                      <input type="checkbox" checked={form.streamIds.includes(s.id)} onChange={() => toggleStream(s.id)} className="rounded" />
                      <div>
                        <p className="text-sm font-medium">{s.title || s.name}</p>
                        <p className="text-xs text-neutral-500">{s.serviceName}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isPublic} onChange={e => updateForm('isPublic', e.target.checked)} className="rounded" />
                Public event
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    live: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300',
    upcoming: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
    ended: 'bg-neutral-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${styles[status] || styles.upcoming}`}>
      {status === 'live' && '● '}{status}
    </span>
  );
}

function FormField({ label, value, onChange, placeholder, required, multiline }) {
  const cls = "w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <div>
      <label className="block text-xs font-medium text-neutral-500 mb-1">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} rows={3} className={cls} />
      ) : (
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} className={cls} />
      )}
    </div>
  );
}

export default EventsPage;
